export type GrayFamily = 'neutral' | 'cool' | 'slate' | 'warm' | 'true';

export interface GrayAnchor {
  /** OKLCH chroma (≥ 0). 0 = achromatic. */
  chroma: number;
  /** OKLCH hue in degrees [0, 360). Ignored when chroma = 0. */
  hue: number;
}

interface ICuratedHues {
  grayFamily(family: GrayFamily): GrayAnchor;
}

/**
 * Gray family OKLCH anchors per design §5. Each family carries a fixed
 * chroma + hue, applied flat to every gray stop. Lightness comes
 * from the gray-scale curve in PaletteEngine.
 *   - 'neutral': barely-there cool tint (chroma ≈ 0.003, hue 270°)
 *   - 'cool':    navy-tinted gray (chroma ≈ 0.02, hue 265°)
 *   - 'slate':   distinctly bluer (chroma ≈ 0.03, hue 220°)
 *   - 'warm':    stone-like (chroma ≈ 0.015, hue 70°)
 *   - 'true':    achromatic (chroma 0)
 */
const ANCHORS: Readonly<Record<GrayFamily, GrayAnchor>> = {
  cool: { chroma: 0.02, hue: 265 },
  neutral: { chroma: 0.003, hue: 270 },
  slate: { chroma: 0.03, hue: 220 },
  true: { chroma: 0, hue: 0 },
  warm: { chroma: 0.015, hue: 70 }
};

export const CuratedHues: ICuratedHues = {
  /**
   * Resolve the OKLCH (chroma, hue) anchor for a named gray family.
   * @param family Family name.
   * @returns Frozen anchor; chroma is 0 for `'true'`.
   */
  grayFamily(family: GrayFamily): GrayAnchor {
    return ANCHORS[family];
  }
};
